import { inject, Injectable } from '@angular/core';
import { assertIsNil, assertIsNotNil } from '@pct-ts/assert';
import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../ui/message-dialog.component';
import { isDialogMessageOptions } from '../type/is-dialog-message-options';
import { isToastMessageOptions } from '../type/is-toast-message-options';
import type { MessageOptions, MessageType } from '../type/message-options';
import type { MessageResult } from '../type/message-result';
import { map, type Observable, of, race, take } from 'rxjs';
import { type IndividualConfig, type ToastrIconClasses, ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@jsverse/transloco';
import { translateTranslateAbleText } from '@pct-ng/i18n';
import type { RemoveIndexSignature } from '@pct-ts/typing';
import { isNil } from '@pct-ts/typeguard';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
@Injectable({ providedIn: 'root' })
export class MessageService {
  static #INSTANCE: MessageService | undefined = undefined;
  static get INSTANCE(): MessageService {
    assertIsNotNil(
      MessageService.#INSTANCE,
      'MessageService instance must be initialized before accessing',
    );
    return MessageService.#INSTANCE;
  }
  readonly #translocoService = inject(TranslocoService);
  readonly #matDialog = inject(MatDialog);
  readonly #toastr = inject(ToastrService);

  // TODO start in app config
  constructor() {
    assertIsNil(
      MessageService.#INSTANCE,
      'MessageService instance should not be already initialized',
    );
    MessageService.#INSTANCE = this;
  }

  open(options: MessageOptions): Observable<MessageResult | undefined> {
    if (isDialogMessageOptions(options)) {
      return this.#matDialog
        .open<
          MessageDialogComponent,
          MessageOptions,
          MessageResult
        >(MessageDialogComponent, { ...options, data: options })
        .afterClosed();
    }

    if (isToastMessageOptions(options)) {
      const message = translateTranslateAbleText(options.message, this.#translocoService);
      const title = translateTranslateAbleText(options.title, this.#translocoService);
      const toastOptions = {
        timeOut: 3000,
        closeButton: options.buttons?.submit?.show !== false,
        disableTimeOut: 'extendedTimeOut',
      } satisfies Partial<IndividualConfig>;

      const activeToast = this.#toastr.show(
        message,
        title,
        toastOptions,
        `toast-${this.#convertMessageTypeToToastType(options.messageType)}`,
      );
      if (isNil(activeToast)) {
        return of(undefined);
      }
      return race([activeToast.onHidden, activeToast.onAction]).pipe(
        take(1),
        map(() => ({ result: 'SUBMIT' })),
      );
    }

    return of(undefined);
  }

  #convertMessageTypeToToastType(
    messageType: MessageType,
  ): keyof RemoveIndexSignature<ToastrIconClasses> {
    return messageType.toLowerCase() as Lowercase<MessageType>;
  }
}
